import { useEffect, useLayoutEffect } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

const KEY_PREFIX = "fl:scroll:";

if (
  typeof window !== "undefined" &&
  "scrollRestoration" in window.history
) {
  window.history.scrollRestoration = "manual";
}

function safeGet(key) {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key, value) {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* quota / disabled */
  }
}

export default function useScrollRestoration() {
  const location = useLocation();
  const navigationType = useNavigationType();

  // Persist scroll position for the current location while the user scrolls.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const storageKey = `${KEY_PREFIX}${location.key}`;
    let scheduled = false;
    const onScroll = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        safeSet(storageKey, String(window.scrollY));
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, [location.key]);

  // Apply the appropriate scroll on each navigation: restore on POP, top on PUSH/REPLACE.
  useLayoutEffect(() => {
    if (typeof window === "undefined") return undefined;

    if (navigationType !== "POP") {
      window.scrollTo(0, 0);
      return undefined;
    }

    const saved = safeGet(`${KEY_PREFIX}${location.key}`);
    if (saved == null) return undefined;
    const targetY = parseInt(saved, 10);
    if (!Number.isFinite(targetY) || targetY <= 0) return undefined;

    let attempts = 0;
    let cancelled = false;
    const tryScroll = () => {
      if (cancelled) return;
      const docH = document.documentElement.scrollHeight;
      const ready = docH >= targetY + window.innerHeight - 200;
      if (ready || attempts > 80) {
        window.scrollTo(0, targetY);
        return;
      }
      attempts += 1;
      requestAnimationFrame(tryScroll);
    };
    tryScroll();
    return () => {
      cancelled = true;
    };
  }, [location.key, navigationType]);
}
